import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { SiteService } from '../services/siteService';
import { SwipeableView } from './SwipeableView';

export const SiteProxy: React.FC = () => {
  const { siteId } = useParams<{ siteId: string }>();
  const location = useLocation();
  const [site, setSite] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // プレビューモードかどうかを判定（URLに/admin/sites/.../previewが含まれているか）
  const isPreviewMode = location.pathname.includes('/admin/sites/') && location.pathname.includes('/preview');

  useEffect(() => {
    const fetchSiteById = async () => {
      if (!siteId) {
        setError('サイトIDが指定されていません。');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        console.log('SiteProxy: サイト取得開始', { siteId, isPreviewMode });
        // まず全サイトを取得して、カスタムドメインやサイトIDでマッチングを試す
        const allSites = await SiteService.getSites();
        console.log('SiteProxy: 取得したサイト数', allSites.length);
        
        // カスタムドメインまたはサイトIDでマッチング
        const foundSite = allSites.find(site => {
          // カスタムドメインでマッチング
          if (site.customDomain && site.customDomain === siteId) {
            return true;
          }
          // サイトIDでマッチング
          if (site.id === siteId) {
            return true;
          }
          return false;
        });
        
        if (foundSite) {
          if (foundSite.status === 'published' || isPreviewMode) {
            setSite(foundSite);
          } else {
            setError(`サイトは存在しますが、公開されていません。現在のステータス: ${foundSite.status}`);
          }
        } else {
          console.log('SiteProxy: サイトが見つかりません', { 
            siteId, 
            availableSites: allSites.map(s => ({ id: s.id, title: s.title, customDomain: s.customDomain }))
          });
          setError(`サイトが見つかりません。利用可能なサイトID: ${allSites.map(s => s.id).join(', ')}`);
        }
      } catch (err) {
        console.error('SiteProxy: サイトの取得に失敗しました:', err);
        console.error('SiteProxy: エラー詳細:', {
          name: err instanceof Error ? err.name : 'Unknown',
          message: err instanceof Error ? err.message : String(err),
          stack: err instanceof Error ? err.stack : undefined
        });
        setError(`サイトの読み込み中にエラーが発生しました: ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        setLoading(false);
      }
    };

    fetchSiteById();
  }, [siteId]);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column'
      }}>
        <Typography variant="h5" color="error" gutterBottom>
          エラー
        </Typography>
        <Typography variant="body1">
          {error}
        </Typography>
      </Box>
    );
  }

  if (!site) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh'
      }}>
        <Typography variant="h5">
          サイトが見つかりません
        </Typography>
      </Box>
    );
  }

  return <SwipeableView pages={site.pages} site={site} isPreviewMode={isPreviewMode} />;
};
